import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useOrg } from '../contexts/OrgContext';
import { hasActiveStripeSub } from '../lib/billing';
import { Plan, PlanOptions, PlanOptionsSkeleton, isDefaultPlan } from '../components/auth/PlanOptions';
import { cheapestImage, cheapestVideo, planProductFeatures } from '../lib/planAllowance';
import { CreditProgress, ErrorState, Notice, Skel, SkeletonRegion } from '../components/ui/PageState';

const CHEAPEST_VIDEO = cheapestVideo();
const CHEAPEST_IMAGE = cheapestImage();

export default function SubscribePlan() {
  const { org, role, loading: orgLoading } = useOrg();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [readyModels, setReadyModels] = useState<number | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [extraSeats, setExtraSeats] = useState(0);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [plansError, setPlansError] = useState(false);

  const canSubscribe = role === 'admin' || role === 'fin_ops';
  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  const loadPlans = useCallback(async () => {
    setPlansLoaded(false);
    setPlansError(false);
    const [plansRes, modelsRes] = await Promise.all([
      supabase.from('plans').select('*').order('monthly_price', { ascending: true }),
      supabase.from('video_templates').select('id', { count: 'exact', head: true }).eq('status', 'published'),
    ]);
    if (plansRes.error) {
      setPlansError(true);
    } else if (plansRes.data) {
      setPlans(plansRes.data);
      const wanted = org?.pending_plan_id || org?.plan_id;
      const preferred = wanted && plansRes.data.some((p) => p.id === wanted)
        ? wanted
        : plansRes.data.find(isDefaultPlan)?.id || plansRes.data[0]?.id || '';
      setSelectedPlanId(preferred);
    }
    if (!modelsRes.error && typeof modelsRes.count === 'number') setReadyModels(modelsRes.count);
    setPlansLoaded(true);
  }, [org?.plan_id, org?.pending_plan_id]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  if (orgLoading || !plansLoaded) {
    return (
      <div className="glass-card subscribe-card">
        <SkeletonRegion label="Carregando planos…">
          <div className="sk-stack" style={{ alignItems: 'center', marginBottom: '1.5rem' }}>
            <Skel className="sk-circle" w={56} h={56} />
            <Skel w="55%" h={26} />
            <Skel w="85%" h={14} />
          </div>
          <PlanOptionsSkeleton />
        </SkeletonRegion>
      </div>
    );
  }

  if (!canSubscribe) {
    return <Navigate to="/app" replace />;
  }

  if (hasActiveStripeSub(org)) {
    return <Navigate to="/pagamento" replace />;
  }

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      setError('Escolha um plano para continuar.');
      return;
    }
    setSubscribing(true);
    setError(null);
    try {
      const body: { plan_id: string; extra_seats?: number } = { plan_id: selectedPlan.id };
      if (selectedPlan.name === 'Empresarial') {
        body.extra_seats = Math.max(0, extraSeats);
      }
      const { data, error: checkoutError } = await supabase.functions.invoke('create-checkout-session', {
        body,
      });
      if (checkoutError) throw checkoutError;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('URL de checkout não retornada');
    } catch (err) {
      console.error('Erro ao iniciar assinatura:', err);
      setError(err instanceof Error ? err.message : 'Não foi possível iniciar o pagamento.');
      setSubscribing(false);
    }
  };

  const productFeatures = planProductFeatures(readyModels);

  return (
    <div className="glass-card subscribe-card">
      <div className="auth-brand">
        <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
      </div>
      <h1>Escolha seu plano</h1>
      <p className="subtitle">
        Sua organização está criada. Assine para liberar créditos mensais. Créditos avulsos ficam disponíveis depois da assinatura.
      </p>
      <p className="subscribe-rate-note">
        1 crédito ≈ US$0.01 de orçamento de API. Vídeos e imagens no modelo mais barato: {CHEAPEST_VIDEO.label}{' '}
        {CHEAPEST_VIDEO.resolution} ({CHEAPEST_VIDEO.seconds}s = {CHEAPEST_VIDEO.credits} créditos) ou{' '}
        {CHEAPEST_IMAGE.label} ({CHEAPEST_IMAGE.credits} créditos). Os agentes de IA (Estúdio, Tela em branco e Editor)
        debitam só o turno, por tokens — em geral uma fração de crédito — e a geração sai do mesmo saldo.
      </p>

      {plansError ? (
        <ErrorState
          flat
          title="Não foi possível carregar os planos"
          message="Nada foi cobrado. Tente carregar a lista de novo."
          onRetry={() => void loadPlans()}
        />
      ) : (
        <>
          {error && (
            <Notice tone="error" className="subscribe-notice">
              <strong>Não foi possível abrir o pagamento.</strong> {error} Você ainda não foi cobrado.
            </Notice>
          )}

          <PlanOptions
            plans={plans}
            selectedId={selectedPlanId}
            onSelect={setSelectedPlanId}
            featuresOf={(p) => [
              ...productFeatures,
              ...(p.can_invite_users ? ['Equipe + assentos extras'] : []),
            ]}
            disabled={subscribing}
            legend="Planos disponíveis"
          />

          {selectedPlan?.name === 'Empresarial' && (
            <div className="form-group subscribe-seats">
              <label htmlFor="subscribe-seats">Assentos extras (+US$19,00 / +500 créditos cada)</label>
              <input
                id="subscribe-seats"
                type="number"
                inputMode="numeric"
                min={0}
                max={50}
                value={extraSeats}
                disabled={subscribing}
                onChange={(e) => setExtraSeats(Math.max(0, parseInt(e.target.value, 10) || 0))}
              />
            </div>
          )}

          {subscribing && (
            <CreditProgress
              label="Abrindo o pagamento seguro…"
              detail="Você será levado ao checkout do Stripe. Nenhuma cobrança acontece até confirmar lá."
            />
          )}

          <button
            type="button"
            className="btn-primary subscribe-cta"
            disabled={subscribing || !selectedPlan}
            onClick={() => void handleSubscribe()}
          >
            {subscribing ? 'Redirecionando…' : `Assinar ${selectedPlan?.name || 'plano'} e ir para o pagamento`}
          </button>
        </>
      )}
    </div>
  );
}
